"use client";
import React, {
  useRef,
  useEffect,
  PropsWithChildren,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Fancybox as NativeFancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import type { OptionsType } from "@fancyapps/ui/types/Fancybox/options";

interface Props {
  delegate?: string;
  options?: Partial<OptionsType>;
  setFancyboxIsActive?: (isActive: boolean) => void;
}

export interface FancyboxRef {
  openFancybox: (index?: number) => void;
}

const Fancybox = forwardRef<FancyboxRef, PropsWithChildren<Props>>(
  function Fancybox(props, ref) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const setFancyboxIsActive = props.setFancyboxIsActive;

    // Expose a method to open Fancybox programmatically
    useImperativeHandle(ref, () => ({
      openFancybox: (index = 0) => {
        const container = containerRef.current;
        if (!container) return;

        // Find all links with data-fancybox attribute
        const items = Array.from(container.querySelectorAll(".f-carousel__slide[data-fancybox]")).map(
          (element) => ({
            src: element.getAttribute("href") || "",
            type: "image",
          })
        );

        if (items.length > 0) {
          // Programmatically open Fancybox with existing images
          NativeFancybox.show(items, { startIndex: index });
        }
      },
    }));

    useEffect(() => {
      const container = containerRef.current;

      const delegate = props.delegate || "[data-fancybox]";
      const options = props.options || {};

      if (container) {
        NativeFancybox.bind(container, delegate, options);  // Bind Fancybox for delegated events
      }

      return () => {
        if (container) {
          NativeFancybox.unbind(container);
        }
        NativeFancybox.close();
      };
    }, [props.delegate, props.options]);

    return <div ref={containerRef}>{props.children}</div>;
  }
);

export default Fancybox;
